import React from 'react'
import { X, Info } from 'lucide-react'

interface RejectionNotificationModalProps {
  isOpen: boolean
  onClose: () => void
  rejectedCount: number
  isBulkAction?: boolean
}

export default function RejectionNotificationModal({ 
  isOpen, 
  onClose, 
  rejectedCount, 
  isBulkAction = false 
}: RejectionNotificationModalProps) {
  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-dark-800 rounded-lg shadow-dark-card border border-dark-700 max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-primary-400 mr-3" />
            <h3 className="text-lg font-medium text-slate-100">
              Drift Rejection Confirmed
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Primary notification message */}
          <div className="bg-primary-500/15 border border-primary-500/30 rounded-md p-4">
            <p className="text-sm text-primary-300">
              This rejection has been <strong className="text-primary-200">documented with your comments</strong> and will be recorded in the audit log. If your organization has a ticketing system configured, a notification will be sent automatically.
            </p>
          </div>

          {/* Count display for bulk actions */}
          {isBulkAction && rejectedCount > 1 && (
            <div className="text-sm text-slate-400">
              <strong className="text-slate-200">{rejectedCount} drifts</strong> have been rejected and documented.
            </div>
          )}

          {/* Remediation information */}
          <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-md p-4">
            <h4 className="text-sm font-medium text-emerald-300 mb-2">
              Manual Remediation
            </h4>
            <p className="text-sm text-emerald-400">
              To manually revert this change in Microsoft 365, review the drift details which show the <strong className="text-emerald-300">Current Value</strong> (what changed) and the <strong className="text-emerald-300">Expected Value</strong> (your baseline). Use this information to restore the setting to its expected state. Once reverted, the drift will automatically show as <strong className="text-emerald-300">"Remediated"</strong> in subsequent scans.
            </p>
          </div>

          {/* Additional context */}
          <div className="text-sm text-slate-400">
            <p>
              Your rejection reason and the user who rejected this drift are permanently recorded for compliance and audit purposes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-dark-900 rounded-b-lg flex justify-end border-t border-dark-700">
          <button
            onClick={onClose}
            autoFocus
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-800 transition-colors"
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  )
}